import { useState, useEffect, useCallback, useRef } from 'react';

export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('ai_tts_muted') === 'true';
    } catch {
      return false;
    }
  });

  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const hasSupport = Boolean(synth);
  const utteranceRef = useRef(null);

  // Clean text and insert natural human breathing pauses and punctuation
  const cleanTextForSpeech = (rawText) => {
    if (!rawText) return '';
    return rawText
      // Convert list formatting to natural speech transitions with pauses
      .replace(/1\.\s+/g, 'First, ')
      .replace(/2\.\s+/g, 'Second, ')
      .replace(/3\.\s+/g, 'Third, ')
      .replace(/4\.\s+/g, 'Fourth, ')
      .replace(/5\.\s+/g, 'Fifth, ')
      .replace(/6\.\s+/g, 'Sixth, ')
      .replace(/7\.\s+/g, 'Seventh, ')
      .replace(/8\.\s+/g, 'Eighth, ')
      .replace(/•\s+/g, ', ')
      // Expand currencies with natural pauses
      .replace(/₹\s*([0-9,.]+)\s*Cr/gi, '$1 Crore Rupees, ')
      .replace(/₹\s*([0-9,.]+)\s*L/gi, '$1 Lakh Rupees, ')
      .replace(/₹\s*([0-9,.]+)/g, '$1 Rupees, ')
      .replace(/£\s*([0-9,.]+)/g, '$1 Pounds, ')
      .replace(/%\b/g, ' per cent')
      // Expand acronyms for clear British pronunciation
      .replace(/\bPO\b/g, 'Purchase Order')
      .replace(/\bPOs\b/g, 'Purchase Orders')
      .replace(/\bPR\b/g, 'Purchase Requisition')
      .replace(/\bPRs\b/g, 'Purchase Requisitions')
      .replace(/\bGRN\b/g, 'Goods Receipt Note')
      .replace(/\bGRNs\b/g, 'Goods Receipt Notes')
      .replace(/\bAP\b/g, 'Accounts Payable')
      .replace(/\bAR\b/g, 'Accounts Receivable')
      .replace(/\bGL\b/g, 'General Ledger')
      .replace(/\bSKU\b/g, 'S K U')
      .replace(/\bSKUs\b/g, 'S K Us')
      .replace(/\bGRC\b/g, 'Governance, Risk and Compliance')
      .replace(/\bSLA\b/g, 'Service Level Agreement')
      .replace(/\bSLAs\b/g, 'Service Level Agreements')
      .replace(/\bRFQ\b/g, 'Request for Quotation')
      .replace(/\bRFQs\b/g, 'Requests for Quotations')
      .replace(/\bRBAC\b/g, 'Role Based Access Control')
      .replace(/\bGSTIN\b/g, 'G S T I N')
      .replace(/\bGST\b/g, 'G S T')
      .replace(/\bTDS\b/g, 'T D S')
      .replace(/\bPAN\b/g, 'P A N')
      .replace(/\bVAT\b/g, 'V A T')
      .replace(/\bPDF\b/g, 'P D F')
      .replace(/\bCSV\b/g, 'C S V')
      .replace(/\bCXO\b/g, 'C X O')
      .replace(/\bQC\b/g, 'Quality Check')
      .replace(/\bUI\b/g, 'User Interface')
      // Clean markdown tags & URLs
      .replace(/[*_#`~[\]()]/g, '')
      .replace(/https?:\/\/\S+/g, '')
      // Remove emojis
      .replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
      // Smooth out consecutive punctuation and spaces
      .replace(/,\s*,+/g, ',')
      .replace(/\.\s*\.+/g, '.')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const cancel = useCallback(() => {
    if (synth) {
      try {
        synth.cancel();
      } catch (e) {}
    }
    setIsSpeaking(false);
    setIsPaused(false);
  }, [synth]);

  const pause = useCallback(() => {
    if (synth) {
      try {
        synth.pause();
      } catch (e) {}
      setIsPaused(true);
    }
  }, [synth]);

  const resume = useCallback(() => {
    if (synth) {
      try {
        synth.resume();
      } catch (e) {}
      setIsPaused(false);
    }
  }, [synth]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      resume();
    } else if (isSpeaking) {
      pause();
    }
  }, [isPaused, isSpeaking, pause, resume]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ai_tts_muted', String(next));
      } catch {}
      if (next) {
        cancel();
      }
      return next;
    });
  }, [cancel]);

  // Find natural UK Accent Female voice
  const getUKFemaleVoice = useCallback(() => {
    if (!synth) return null;
    const voices = synth.getVoices();
    if (!voices || voices.length === 0) return null;

    const femaleKeywords = [
      'female', 'google uk english female', 'serena', 'stephanie', 
      'libby', 'sonia', 'martha', 'victoria', 'kate', 'fiona', 
      'moira', 'alice', 'hazel', 'susan', 'emma', 'amy', 'olivia'
    ];

    // 1. Highest priority: UK English (en-GB) with explicit female name
    const ukFemale = voices.find(v => {
      const isUK = v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb');
      if (!isUK) return false;
      const name = v.name.toLowerCase();
      return femaleKeywords.some(k => name.includes(k));
    });

    if (ukFemale) return ukFemale;

    // 2. Second priority: Any UK English voice (en-GB) that is not explicitly male
    const ukVoice = voices.find(v => {
      const isUK = v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb');
      if (!isUK) return false;
      const name = v.name.toLowerCase();
      return !name.includes('male') && !name.includes('george') && !name.includes('daniel') && !name.includes('oliver');
    });

    if (ukVoice) return ukVoice;

    // 3. Any UK voice
    const anyUK = voices.find(v => v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb'));
    if (anyUK) return anyUK;

    // 4. Natural Commonwealth / English female fallback
    const naturalFemale = voices.find(v => {
      if (!v.lang || !v.lang.toLowerCase().startsWith('en')) return false;
      const name = v.name.toLowerCase();
      return femaleKeywords.some(k => name.includes(k)) || name.includes('samantha') || name.includes('karen');
    });

    return naturalFemale || voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en')) || null;
  }, [synth]);

  const speak = useCallback((text) => {
    if (!hasSupport || isMuted || !text) return;

    cancel();

    const clean = cleanTextForSpeech(text);
    if (!clean) return;

    const utterance = new SpeechSynthesisUtterance(clean);
    
    // Human-like UK female parameters: composed, articulate cadence with natural melodic pitch
    utterance.rate = 0.94;
    utterance.pitch = 1.10;
    utterance.lang = 'en-GB';

    const selectedVoice = getUKFemaleVoice();
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      if (selectedVoice.lang) utterance.lang = selectedVoice.lang;
    }

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };
    utterance.onpause = () => setIsPaused(true);
    utterance.onresume = () => setIsPaused(false);
    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };
    utterance.onerror = (e) => {
      console.warn('Speech synthesis error:', e);
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utteranceRef.current = utterance;
    try {
      synth.speak(utterance);
    } catch (e) {
      console.warn('TTS speak execution error:', e);
      setIsSpeaking(false);
      setIsPaused(false);
    }
  }, [hasSupport, isMuted, synth, cancel, getUKFemaleVoice]);

  useEffect(() => {
    if (synth && synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = () => {
        // Voices loaded
      };
    }
    return () => {
      if (synth) {
        try {
          synth.cancel();
        } catch (e) {}
      }
    };
  }, [synth]);

  return {
    speak,
    cancel,
    stop: cancel,
    pause,
    resume,
    togglePause,
    isSpeaking,
    isPaused,
    isMuted,
    toggleMute,
    hasSupport
  };
}
